import React from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import { Alert } from "components/ui/alert";
import { Button } from "components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { AccessControlGroupResponse, AccessControlGroupType } from "api/ApiClient";
import { apiClientFactory, getExternalTenantId } from "helpers";
import { useSelector } from "react-redux";
import { ApplicationState } from "store";

type Props = {
  open: boolean;
  onClose: () => void;
  onCreated: (group: AccessControlGroupResponse) => void;
};

type FormState = {
  externalId: string;
  propertyExternalId: string;
  unitNumber: string;
  type: AccessControlGroupType;
  name: string;
  description: string;
};

const initialState: FormState = {
  externalId: "",
  propertyExternalId: "",
  unitNumber: "",
  type: AccessControlGroupType.Regular,
  name: "",
  description: "",
};

export default function RegisterGroupModal({ open, onClose, onCreated }: Props) {
  const me = useSelector((state: ApplicationState) => state.me);
  const [formState, setFormState] = React.useState<FormState>(initialState);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!open) {
      setFormState(initialState);
      setSaving(false);
      setError(null);
    }
  }, [open]);

  const submit = async () => {
    if (!formState.name) {
      setError("Name is required.");
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const created = await apiClientFactory().postAccessControlGroup({
        externalId: formState.externalId.trim() || undefined,
        tenantExternalId: getExternalTenantId(me.user),
        propertyExternalId: formState.propertyExternalId || undefined,
        unitNumber:
          formState.type === AccessControlGroupType.Unit
            ? formState.unitNumber || undefined
            : undefined,
        type: formState.type,
        name: formState.name,
        description: formState.description || undefined,
      });

      onCreated(created);
    } catch (e: any) {
      setError(e?.message || "Unable to create access control group.");
    } finally {
      setSaving(false);
    }
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen && !saving) {
      onClose();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        aria-describedby={undefined}
        showCloseButton={false}
        className="sm:max-w-lg"
        onEscapeKeyDown={(event) => {
          if (saving) {
            event.preventDefault();
          }
        }}
        onPointerDownOutside={(event) => {
          if (saving) {
            event.preventDefault();
          }
        }}
      >
        <DialogHeader>
          <DialogTitle>Create Access Control Group</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4">
          {error ? <Alert variant="destructive">{error}</Alert> : null}
          <div className="grid gap-2">
            <Label htmlFor="group-external-id">Unique Name (optional)</Label>
            <Input
              id="group-external-id"
              placeholder="Auto-generated if left blank"
              value={formState.externalId}
              onChange={(event) =>
                setFormState((prev) => ({ ...prev, externalId: event.target.value }))
              }
            />
            <p className="text-xs text-muted-foreground">
              Optional. If left blank, a unique ID is generated automatically.
            </p>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="group-name">Name</Label>
            <Input
              id="group-name"
              required
              value={formState.name}
              onChange={(event) =>
                setFormState((prev) => ({ ...prev, name: event.target.value }))
              }
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="group-type">Type</Label>
            <Select
              value={formState.type}
              onValueChange={(type) =>
                setFormState((prev) => ({
                  ...prev,
                  type: type as AccessControlGroupType,
                }))
              }
            >
              <SelectTrigger id="group-type" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.values(AccessControlGroupType).map((type) => (
                  <SelectItem key={type} value={type}>
                    {type}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="group-description">Description</Label>
            <Input
              id="group-description"
              value={formState.description}
              onChange={(event) =>
                setFormState((prev) => ({ ...prev, description: event.target.value }))
              }
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="group-property-external-id">Property External Id</Label>
            <Input
              id="group-property-external-id"
              value={formState.propertyExternalId}
              onChange={(event) =>
                setFormState((prev) => ({
                  ...prev,
                  propertyExternalId: event.target.value,
                }))
              }
            />
          </div>
          {formState.type === AccessControlGroupType.Unit ? (
            <div className="grid gap-2">
              <Label htmlFor="group-unit-number">Unit Number</Label>
              <Input
                id="group-unit-number"
                value={formState.unitNumber}
                onChange={(event) =>
                  setFormState((prev) => ({ ...prev, unitNumber: event.target.value }))
                }
              />
            </div>
          ) : null}
        </div>
        <DialogFooter>
          <Button disabled={saving} onClick={onClose} type="button" variant="outline">
            Cancel
          </Button>
          <Button disabled={saving} onClick={submit} type="button">
            {saving ? "Creating..." : "Create Group"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
